import { useState, forwardRef } from 'react';
import { Combobox } from '@headlessui/react';
import Avatar from '../features/avatar/Avatar';
import { twMerge } from 'tailwind-merge';

interface SelectProps<T> {
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  options: T[];
  labelKey?: keyof T;
  placeholder?: string;
  placeholderClassName?: string;
  onChange?: (value: T | null) => void;
  hideInput?: boolean;
  defaultSelectedLabelKey?: string;
  containerClassName?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps<any>>(
  (
    {
      disabled = false,
      options,
      labelKey,
      fullWidth = true,
      placeholder,
      placeholderClassName,
      onChange,
      hideInput = false,
      defaultSelectedLabelKey,
      containerClassName,
    },
    ref,
  ) => {
    const initialSelectedOption =
      defaultSelectedLabelKey && labelKey
        ? options.find((option) => option[labelKey] === defaultSelectedLabelKey)
        : null;
    const [selectedOption, setSelectedOption] = useState<any>(initialSelectedOption);

    const [query, setQuery] = useState('');

    const handleSelectChange = (value: any) => {
      setSelectedOption(value);
      if (onChange) {
        onChange(value);
      }
    };

    const getDisplayValue = (option: any) => {
      if (!option) return '';
      return labelKey ? option[labelKey] : option;
    };

    return (
      <div className={twMerge('relative', `${fullWidth ? 'w-full' : 'inline-block'}`, containerClassName)}>
        <Combobox value={selectedOption} onChange={handleSelectChange} disabled={disabled}>
          <div className="relative w-full">
            <Combobox.Input
              onChange={(event) => setQuery(event.target.value)}
              className={twMerge(
                'w-full overflow-hidden border-transparent !outline-none focus:border-transparent focus:ring-0 ' +
                  'h-12 rounded-lg bg-ph-gray-field py-3 pl-3 text-sm font-normal tracking-wide text-white !outline-none focus:ring-0',
                placeholderClassName,
              )}
              displayValue={getDisplayValue}
              placeholder={placeholder}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <svg
                className="h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </Combobox.Button>
          </div>
          <Combobox.Options className="absolute z-[11] mt-1 w-full rounded-b-lg bg-ph-gray-field py-3 text-sm font-normal text-white">
            {!hideInput &&
              query.length > 0 &&
              !options.some((option) => (labelKey ? option[labelKey] === query : option === query)) && (
                <Combobox.Option value={labelKey ? { [labelKey]: query } : query}>
                  <p className="mb-2 overflow-auto overflow-ellipsis pl-10 hover:bg-ph-primary-violet">{query}</p>
                </Combobox.Option>
              )}
            {options.map((option, index) => (
              <Combobox.Option
                key={index}
                value={option}
                className="relative flex cursor-default select-none items-center gap-2 bg-ph-gray-field py-2 pl-2 text-sm font-normal text-white hover:cursor-pointer hover:bg-ph-gray-evening"
              >
                {labelKey ? (
                  <>
                    <Avatar
                      avatar2D
                      round={false}
                      userId={option.id}
                      className="h-8 w-8 rounded-md border border-ph-primary-violet"
                    />
                    <p>{option[labelKey]}</p>
                  </>
                ) : (
                  <p>{option}</p>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Combobox>
      </div>
    );
  },
);

export default Select;
